import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/ui/AppIcon';
import { AUSTRALIAN_CITIES } from '@/data/australianCities';
import { fetchUpcomingEvents, mediaUrl } from '@/lib/api';
import { isEventBookingOpen, sortEventsByDateWithExpiredLast } from '@/lib/eventAdapter';

const categoryColors = {
    Theatre: 'bg-purple-100 text-purple-800 border-purple-300',
    Film: 'bg-blue-100 text-blue-800 border-blue-300',
    Music: 'bg-amber-100 text-amber-800 border-amber-300',
};

function FilterPill({ active, onClick, children }) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            onClick={onClick}
            className={`category-pill px-5 py-2 rounded-full text-sm font-semibold border tracking-wide transition-all ${
                active
                    ? 'bg-primary text-accent-foreground border-primary'
                    : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
            }`}
        >
            {children}
        </button>
    );
}

export default function EventsGrid() {
    const [activeCategory, setActiveCategory] = useState('All');
    const [activeCity, setActiveCity] = useState('All');
    const [allEvents, setAllEvents] = useState([]);
    const sectionRef = useRef(null);

    useEffect(() => {
        fetchUpcomingEvents()
            .then((events) => setAllEvents(sortEventsByDateWithExpiredLast(events)))
            .catch(() => setAllEvents([]));
    }, []);

    const categories = ['All', ...new Set(allEvents.map((event) => event.category).filter(Boolean))];

    const filtered = allEvents.filter((event) => {
        const categoryMatch = activeCategory === 'All' || event.category === activeCategory;
        const cities = event.filterCities?.length ? event.filterCities : [event.filterCity].filter(Boolean);
        const cityMatch = activeCity === 'All' || cities.includes(activeCity);
        return categoryMatch && cityMatch;
    });

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.querySelectorAll('.reveal-up').forEach((el) => {
                        el.classList.add('revealed');
                    });
                }
            });
        }, { threshold: 0.05 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            sectionRef.current?.querySelectorAll('.reveal-up').forEach((el) => {
                el.classList.add('revealed');
            });
        });

        return () => cancelAnimationFrame(frame);
    }, [allEvents, activeCategory, activeCity, filtered.length]);

    function resetFilters() {
        setActiveCategory('All');
        setActiveCity('All');
    }

    return (
        <div ref={sectionRef} className="bg-background py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="reveal-up flex flex-wrap items-center gap-3 mb-10">
                    <div className="flex flex-wrap items-center gap-3 flex-1" role="tablist" aria-label="Event categories">
                        {categories.map((cat) => (
                            <FilterPill
                                key={cat}
                                active={activeCategory === cat}
                                onClick={() => setActiveCategory(cat)}
                            >
                                {cat}
                                {cat !== 'All' ? (
                                    <span className="ml-2 text-xs opacity-60">
                                        ({allEvents.filter((event) => event.category === cat).length})
                                    </span>
                                ) : null}
                            </FilterPill>
                        ))}
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <label htmlFor="city-filter" className="text-sm font-medium text-muted-foreground shrink-0">
                            City
                        </label>
                        <select
                            id="city-filter"
                            value={activeCity}
                            onChange={(e) => setActiveCity(e.target.value)}
                            className="w-full sm:w-52 px-4 py-2.5 rounded-full text-sm font-semibold border border-border bg-background text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary cursor-pointer"
                        >
                            <option value="All">All cities</option>
                            {AUSTRALIAN_CITIES.map((city) => (
                                <option key={city} value={city}>
                                    {city}
                                    {allEvents.some((event) => (event.filterCities ?? [event.filterCity]).includes(city))
                                        ? ` (${allEvents.filter((event) => (event.filterCities ?? [event.filterCity]).includes(city)).length})`
                                        : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <span className="text-sm text-muted-foreground hidden lg:block shrink-0">
                        {filtered.length} event{filtered.length !== 1 ? 's' : ''} found
                    </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filtered.map((event, i) => (
                        <div
                            key={event.id}
                            className="reveal-up event-card-hover group relative rounded-xl overflow-hidden border border-border/50 aspect-[3/4] bg-black"
                            data-testid="event-card"
                            style={{ transitionDelay: `${i % 3 * 80}ms` }}
                        >
                            <img
                                src={mediaUrl(event.image)}
                                alt={event.alt}
                                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10 pointer-events-none" />

                            <span className={`absolute top-3 left-3 z-10 text-xs font-semibold px-2.5 py-1 rounded-full border ${categoryColors[event.category] || ''}`}>
                                {event.category}
                            </span>

                            <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col gap-3 p-5 bg-transparent">
                                <p className="text-primary text-xs font-bold tracking-wide uppercase">{event.date}</p>
                                <div>
                                    <h3 className="font-display text-xl font-semibold text-white mb-1 drop-shadow-sm">{event.title}</h3>
                                    <p className="text-white/75 text-sm">{event.subtitle}</p>
                                </div>

                                {(event.venue || event.city || event.presenter) ? (
                                    <div className="space-y-2 text-sm text-white/80">
                                        {event.venue ? (
                                            <div className="flex items-center gap-2">
                                                <Icon name="MapPinIcon" size={13} className="text-primary shrink-0" />
                                                <span className="truncate">
                                                    {event.hasMultipleLocations ? `${event.venues?.length ?? 0} locations` : event.venue}
                                                </span>
                                            </div>
                                        ) : null}
                                        {event.city ? (
                                            <div className="flex items-center gap-2">
                                                <Icon name="BuildingLibraryIcon" size={13} className="text-primary shrink-0" />
                                                <span>{event.city}</span>
                                            </div>
                                        ) : null}
                                        {event.presenter ? (
                                            <div className="flex items-center gap-2">
                                                <Icon name="UserIcon" size={13} className="text-primary shrink-0" />
                                                <span>{event.presenter}</span>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}

                                <div className="flex items-center justify-between pt-3 border-t border-white/20">
                                    <span className="text-primary font-bold text-sm">{event.price}</span>
                                    {isEventBookingOpen(event) ? (
                                        <Link to={`/events/${event.slug}`} className="btn-gold flex items-center gap-1.5 px-6 py-2.5 rounded-full text-xs font-bold tracking-widest uppercase">
                                            <span>BOOK MY SEAT</span>
                                            <Icon name="ArrowRightIcon" size={11} className="text-black" />
                                        </Link>
                                    ) : (
                                        <span
                                            aria-disabled="true"
                                            className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-xs font-bold tracking-widest uppercase text-black/60 opacity-60 cursor-not-allowed"
                                            style={{ background: 'linear-gradient(135deg, #ebb413, #f0c030)' }}
                                        >
                                            <span>BOOK MY SEAT</span>
                                            <Icon name="ArrowRightIcon" size={11} />
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {filtered.length === 0 ? (
                    <div className="text-center py-20">
                        <Icon name="CalendarDaysIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground text-lg">No events found for these filters.</p>
                        <button type="button" onClick={resetFilters} className="btn-outline-gold mt-4 px-6 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
                            View All Events
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
